import React, { useEffect, useState, useCallback } from 'react';
import { departmentApi, planApi } from '../api';
import { Btn, Alert, Spinner, EmptyState } from '../components/ui';
import PlanLimitModal from '../components/PlanLimitModal';
import UsageIndicator from '../components/UsageIndicator';

export default function Departments() {
  const [departments, setDepts]    = useState([]);
  const [loading,     setLoading]  = useState(true);
  const [name,        setName]     = useState('');
  const [error,       setError]    = useState('');
  const [success,     setSuccess]  = useState('');
  const [adding,      setAdding]   = useState(false);
  const [usage,       setUsage]    = useState(null);
  const [planError,   setPlanError]= useState(null);

  const load = useCallback(() => {
    Promise.all([departmentApi.getAll(), planApi.getUsage()])
      .then(([d, p]) => {
        setDepts(d.data.data || []);
        setUsage(p.data.data);
      })
      .catch(() => setError('Failed to load departments'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(''); setSuccess(''); setAdding(true);
    try {
      const res = await departmentApi.create(trimmed);
      if (res.data.success) {
        setDepts(prev => [...prev, res.data.data]);
        setName('');
        setSuccess('Department created');
        load(); // refresh usage
      }
    } catch (e) {
      if (e.response?.status === 402) {
        setPlanError(e.response.data);
      } else {
        setError(e.response?.data?.message || 'Failed to create department');
      }
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    setError(''); setSuccess('');
    try {
      await departmentApi.delete(id);
      setDepts(prev => prev.filter(d => d.id !== id));
      setSuccess('Department deleted');
    } catch (e) {
      setError(e.response?.data?.message || 'Cannot delete this department');
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <h1 style={S.pageTitle}>Departments</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
          Departments cannot be renamed or deleted once they have history.
        </p>
        {usage && (
          <UsageIndicator
            current={usage.departments}
            max={usage.maxDepartments}
            label="Department slots"
          />
        )}
      </div>

      {success && <Alert type="success" message={success} />}
      {error   && <Alert type="error"   message={error}   />}

      <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '20px', marginTop: '20px' }}>
        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '12px' }}>Create Department</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            placeholder="e.g. Engineering, Sales, Operations"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && create()}
            style={{ flex: 1, padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '15px' }}
          />
          <Btn onClick={create} loading={adding}>Create</Btn>
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
        ) : departments.length === 0 ? (
          <EmptyState icon="🏢" title="No departments yet" subtitle="Create your first department above." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {departments.map(d => (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', background: 'var(--color-surface)',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'var(--color-primary-lighter)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
                  }}>🏢</div>
                  <div>
                    <p style={{ fontWeight: 500, fontSize: '15px' }}>{d.name}</p>
                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                      {d.hasHistory ? 'Has history — cannot be deleted' : 'No history yet'}
                    </p>
                  </div>
                </div>
                {!d.hasHistory && (
                  <button onClick={() => remove(d.id)} style={{
                    background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
                    border: '1px solid #FEB2B2', borderRadius: 'var(--radius-sm)',
                    padding: '6px 12px', fontSize: '13px', cursor: 'pointer',
                  }}>Delete</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {planError && (
        <PlanLimitModal
          error={planError}
          onClose={() => setPlanError(null)}
        />
      )}
    </div>
  );
}

const S = {
  pageTitle:   { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' },
  pageSubtitle:{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '16px' },
};
